import { useCallback } from 'react';

import { useToast } from '@/components/ui/toast';

interface ApiError {
  response?: {
    data?: {
      message?: string;
      error?: string;
    };
    status?: number;
    statusText?: string;
  };
  message?: string;
  code?: string;
}

interface ErrorHandlerOptions {
  /** Custom error message to display instead of the API message */
  customMessage?: string;
  /** Duration to show the error toast (default 7000ms) */
  duration?: number;
  /** Whether to log the error to console (default true in dev) */
  logError?: boolean;
  statusHandlers?: Record<number, string>;
}

const DEFAULT_STATUS_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input.',
  401: 'You are not authenticated. Please log in.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'A conflict occurred. The resource may have been modified.',
  422: 'Validation failed. Please check your input.',
  429: 'Too many requests. Please try again later.',
  500: 'Server error. Please try again later.',
  502: 'Bad gateway. The server is temporarily unavailable.',
  503: 'Service unavailable. Please try again later.',
  504: 'Gateway timeout. The request took too long.',
};

function extractErrorMessage(
  error: unknown,
  options?: ErrorHandlerOptions,
): string {
  if (options?.customMessage) {
    return options.customMessage;
  }

  const apiError = error as ApiError;

  const statusCode = apiError.response?.status;

  if (statusCode && options?.statusHandlers?.[statusCode]) {
    return options.statusHandlers[statusCode];
  }

  if (statusCode && DEFAULT_STATUS_MESSAGES[statusCode]) {
    return DEFAULT_STATUS_MESSAGES[statusCode];
  }

  const apiMessage =
    apiError.response?.data?.message ??
    apiError.response?.data?.error ??
    apiError.response?.statusText;

  if (apiMessage) {
    return apiMessage;
  }

  if (apiError.message) {
    return apiError.message;
  }

  if (!statusCode) {
    return 'Network error. Please check your connection and try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

export const useErrorHandler = () => {
  const { showToast } = useToast();

  const handleError = useCallback(
    (options?: ErrorHandlerOptions) => {
      return (error: unknown) => {
        const message = extractErrorMessage(error, options);
        const duration = options?.duration ?? 7000;

        if ((options?.logError ?? import.meta.env.DEV) && import.meta.env.DEV) {
          console.error('Error caught by error handler:', error);
        }

        showToast(message, 'error', duration);
      };
    },
    [showToast],
  );

  const handleSuccess = useCallback(
    (message: string, duration = 5000) => {
      showToast(message, 'success', duration);
    },
    [showToast],
  );

  const handleInfo = useCallback(
    (message: string, duration = 5000) => {
      showToast(message, 'info', duration);
    },
    [showToast],
  );

  return {
    handleError,
    handleSuccess,
    handleInfo,
  };
};

export type ErrorHandler = ReturnType<typeof useErrorHandler>['handleError'];
